import { ROOT_PATH, ROUTES_CONFIG } from "@config/routes.config";

import { serializeParams } from "./search-params-parsers";

/**
 * Creates a navigation link object with query parameters and optional state.
 * Simple parameters go in URL for shareability, complex objects go in state.
 *
 * @param {string} pathname - The destination pathname
 * @param {Object} search - Parameters to include in query string
 * @param {Object} state - Optional state for complex data that can't be serialized
 * @returns {Object} Navigation link object with pathname, search, and optionally state
 */
export const createNavigationLink = (pathname, search = {}, state = null) => {
  const searchParams = serializeParams(search);

  const link = {
    pathname,
    search: searchParams,
  };

  // Only add state if provided and not empty
  if (state && Object.keys(state).length > 0) {
    link.state = state;
  }

  return link;
};

/**
 * Creates an organization detail link with query parameters
 *
 * @param {string} code - Organization code
 * @param {Object} search - Search parameters
 * @param {Object} state - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createOrganizationLink = ({ code, search, state }) => {
  return createNavigationLink(
    `${ROOT_PATH}${ROUTES_CONFIG.ORGANIZATIONS.path}/${code}`,
    search,
    state,
  );
};

/**
 * Gets a search parameter value from the location object
 * Workaround when nuqs doesn't detect the parameter
 * change because the page is not reloaded
 * or the component is not unmounted/mounted again
 * @param {Object} location - The location object
 * @param {string} param - The parameter name
 * @param {string} defaultValue - The default value if the parameter is not found
 * @returns {string} The search parameter value
 */
export const getSearchParamValue = (location, param, defaultValue) => {
  const searchParams = new URLSearchParams(location.search);
  return searchParams.get(param) || defaultValue;
};
